import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check if we're in the browser environment
    if (typeof window === 'undefined') return false;
    
    // Check localStorage for saved preference first
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
    
    // If no saved preference, respect OS preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark;
  });

  // Apply theme immediately on mount and when changed
  useEffect(() => {
    // Apply theme to document immediately
    const applyTheme = (dark) => {
      if (dark) {
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('light');
        document.documentElement.style.setProperty('color-scheme', 'dark');
      } else {
        document.documentElement.classList.add('light');
        document.documentElement.classList.remove('dark');
        document.documentElement.style.setProperty('color-scheme', 'light');
      }
    };

    // Apply theme immediately
    applyTheme(isDarkMode);
    
    // Save preference to localStorage
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Listen for OS theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only update if user hasn't set a manual preference
      const saved = localStorage.getItem('theme');
      if (!saved) {
        setIsDarkMode(e.matches);
      }
    };

    // Add listener for OS theme changes
    mediaQuery.addListener(handleChange);
    
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const resetToSystemPreference = () => {
    localStorage.removeItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(prefersDark);
  };

  const value = {
    isDarkMode,
    isLightMode: !isDarkMode,
    toggleTheme,
    resetToSystemPreference,
    theme: isDarkMode ? 'dark' : 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Theme-aware color utilities
export const getThemeColors = (isDarkMode) => ({
  // Backgrounds
  bg: {
    primary: isDarkMode ? 'bg-black' : 'bg-white',
    secondary: isDarkMode ? 'bg-neutral-950' : 'bg-neutral-50',
    card: isDarkMode ? 'bg-neutral-900' : 'bg-white',
    glass: isDarkMode ? 'bg-black/70' : 'bg-white/70',
    muted: isDarkMode ? 'bg-neutral-800' : 'bg-neutral-100',
    gradient: isDarkMode ? 'bg-gradient-to-b from-black to-neutral-900' : 'bg-gradient-to-b from-white to-neutral-50'
  },
  
  // Text colors
  text: {
    primary: isDarkMode ? 'text-white' : 'text-black',
    secondary: isDarkMode ? 'text-neutral-200' : 'text-neutral-800',
    muted: isDarkMode ? 'text-neutral-300' : 'text-neutral-600',
    accent: 'text-primary-400', // Orange stays the same
    inverse: isDarkMode ? 'text-black' : 'text-white'
  },
  
  // Borders
  border: {
    default: isDarkMode ? 'border-neutral-700' : 'border-neutral-300',
    muted: isDarkMode ? 'border-neutral-800' : 'border-neutral-200',
    primary: isDarkMode ? 'border-neutral-800' : 'border-neutral-200',
    secondary: isDarkMode ? 'border-neutral-700' : 'border-neutral-300',
    accent: 'border-primary-400'
  },
  
  // Interactive states
  hover: {
    bg: isDarkMode ? 'hover:bg-neutral-800' : 'hover:bg-neutral-100',
    text: isDarkMode ? 'hover:text-white' : 'hover:text-black'
  },
  
  // Form placeholders
  placeholder: isDarkMode ? 'placeholder-neutral-400' : 'placeholder-neutral-500'
});

// Theme-aware class utility
export const themeClass = (darkClass, lightClass, isDarkMode) => {
  return isDarkMode ? darkClass : lightClass;
};